import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { getAppUsers } from '../../lib/apiService';
import { Search, Building, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { MAIN_API_BASE } from '../../lib/apiConfig';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import PropertyDetail from '../../components/PropertyDetail';

const resolveImageUrl = (img, folder = 'profile_pictures') => {
  if (!img || typeof img !== 'string') return '';
  const trimmed = img.trim();
  if (!trimmed || trimmed === 'NULL' || trimmed === 'null') return '';

  const filename = trimmed.split('?')[0].split('#')[0].split('/').pop();
  if (!filename) return '';

  return `${MAIN_API_BASE}/storage/${folder}/${filename}`;
};

const SellersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getAppUsers();
      if (res?.status) {
        setUsers(res.users || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(u => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.toLowerCase().includes(q)
    );
  });

  const handleViewProperties = (user) => {
    setSelectedUser(user);
    if (user.properties && user.properties.length > 0) {
      setSelectedPropertyId(user.properties[0].id.toString());
    } else {
      setSelectedPropertyId('');
    }
  };

  const selectedProperty = selectedUser?.properties?.find(p => p.id.toString() === selectedPropertyId) || selectedUser?.properties?.[0];

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Sellers | JoldiPabo Admin</title>
      </Helmet>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Sellers & Users</h1>
          <p className="text-sm text-slate-600 mt-1">View app users and their listed properties</p>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search users..."
            className="h-10 pl-9 bg-white"
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Gender</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Properties</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden shrink-0 border border-slate-200">
                        {u.profile_picture && u.profile_picture !== 'NULL' && u.profile_picture !== '' ? (
                          <img
                            src={resolveImageUrl(u.profile_picture, 'profile_pictures')}
                            alt={u.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; e.target.nextElementSibling.classList.remove('hidden'); }}
                          />
                        ) : null}
                        <span className={`text-sm font-semibold text-[#ee7923] ${u.profile_picture && u.profile_picture !== 'NULL' && u.profile_picture !== '' ? 'hidden' : ''}`}>
                          {u.name?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <div>{u.email}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{u.phone}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 capitalize">
                    {u.gender || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-slate-100 text-slate-900 hover:bg-slate-200 border-slate-300 h-8"
                      onClick={() => handleViewProperties(u)}
                    >
                      <Building className="w-3 h-3 mr-1.5" />
                      {u.properties?.length || 0} Listed
                    </Button>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {u.created_on ? new Date(u.created_on).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && <div className="text-center py-16 text-slate-500">No users found</div>}
        {users.length > 0 && filteredUsers.length === 0 && <div className="text-center py-16 text-slate-500">No results found</div>}
      </Card>

      {/* Property Detail Side Sheet */}
      {selectedUser && selectedProperty ? (
        <PropertyDetail
          property={selectedProperty}
          allProperties={selectedUser.properties}
          propertyIds={selectedUser.properties.map(p => String(p.id))}
          onPropertyChange={(p) => setSelectedPropertyId(String(p.id))}
          onClose={() => setSelectedUser(null)}
        />
      ) : selectedUser ? (
        <Sheet open={true} onOpenChange={() => setSelectedUser(null)}>
          <SheetContent className="bg-slate-50 sm:max-w-[480px] w-full overflow-y-auto p-0">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {selectedUser?.name}'s Properties
              </h2>
              <button onClick={() => setSelectedUser(null)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-5 py-12 text-center text-slate-500">
              <Building className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p>This user has not listed any properties yet.</p>
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
};

export default SellersPage;
